import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import ScrollToTop from '@/components/feature/ScrollToTop';
import { getArticleBySlug, articles } from '@/mocks/articles';
import NotFound from '@/pages/NotFound';

export default function ArticlePage() {
  const { slug } = useParams<{ slug: string }>();
  const article = slug ? getArticleBySlug(slug) : undefined;

  if (!article) {
    return <NotFound />;
  }

  const currentIndex = articles.findIndex((a) => a.slug === slug);
  const prevArticle = currentIndex > 0 ? articles[currentIndex - 1] : null;
  const nextArticle = currentIndex < articles.length - 1 ? articles[currentIndex + 1] : null;
  const relatedArticles = articles
    .filter((a) => a.categorySlug === article.categorySlug && a.slug !== slug)
    .slice(0, 3);

  return (
    <>
      <ScrollToTop />
      <Navbar />
      <main className="pt-16">
        <article className="w-full px-4 md:px-6 py-8 bg-background-50">
          <Breadcrumb
            items={[
              { label: '彩票资讯', path: '/articles' },
              { label: article.category, path: `/articles` },
              { label: article.title },
            ]}
          />

          <div className="max-w-4xl mx-auto">
            <div className="mb-8">
              <div className="flex items-center gap-3 mb-4">
                <span className="px-3 py-1 rounded-full text-xs font-medium bg-accent-100 text-accent-700">
                  {article.category}
                </span>
                <span className="text-xs text-foreground-500">{article.publishedAt}</span>
              </div>
              <h1 className="font-heading font-bold text-xl md:text-3xl text-foreground-950 mb-4 leading-tight">
                {article.title}
              </h1>
              <div className="flex items-center gap-4 text-xs text-foreground-500 pb-6 border-b border-background-200/70">
                <span className="flex items-center gap-1.5">
                  <i className="ri-user-line"></i>
                  {article.author}
                </span>
                <span className="flex items-center gap-1.5">
                  <i className="ri-links-line"></i>
                  来源：{article.source}
                </span>
              </div>
            </div>

            <div className="mb-8">
              <img
                src={article.thumbnailUrl}
                alt={article.title}
                title={article.title}
                className="w-full rounded-xl object-cover max-h-[400px]"
              />
            </div>

            <div
              className="prose prose-sm md:prose-base max-w-none text-foreground-700 leading-relaxed
                [&_h2]:font-heading [&_h2]:text-xl [&_h2]:font-bold [&_h2]:text-foreground-950 [&_h2]:mt-8 [&_h2]:mb-4
                [&_h3]:font-heading [&_h3]:text-lg [&_h3]:font-semibold [&_h3]:text-foreground-950 [&_h3]:mt-6 [&_h3]:mb-3
                [&_p]:mb-4 [&_p]:leading-relaxed
                [&_ul]:list-disc [&_ul]:pl-6 [&_ul]:mb-4 [&_ul]:space-y-1.5
                [&_ol]:list-decimal [&_ol]:pl-6 [&_ol]:mb-4 [&_ol]:space-y-1.5
                [&_li]:text-foreground-700 [&_li]:leading-relaxed
                [&_strong]:text-foreground-950 [&_strong]:font-semibold
                [&_blockquote]:border-l-4 [&_blockquote]:border-primary-300 [&_blockquote]:pl-4 [&_blockquote]:text-foreground-600 [&_blockquote]:italic
              "
              dangerouslySetInnerHTML={{ __html: article.content }}
            />

            <div className="flex items-center gap-2 flex-wrap mt-8 pt-6 border-t border-background-200/70">
              {article.tags.map((tag) => (
                <span
                  key={tag}
                  className="px-3 py-1 rounded-full text-xs bg-background-100 text-foreground-600"
                >
                  #{tag}
                </span>
              ))}
            </div>

            <div className="flex flex-col sm:flex-row justify-between gap-4 mt-8 pt-6 border-t border-background-200/70">
              {prevArticle ? (
                <Link
                  to={`/article/${prevArticle.slug}`}
                  className="flex-1 group cursor-pointer"
                >
                  <span className="text-xs text-foreground-500">上一篇</span>
                  <p className="text-sm text-foreground-700 group-hover:text-primary-600 transition-colors line-clamp-1 mt-1">
                    {prevArticle.title}
                  </p>
                </Link>
              ) : (
                <div className="flex-1"></div>
              )}
              {nextArticle ? (
                <Link
                  to={`/article/${nextArticle.slug}`}
                  className="flex-1 text-right group cursor-pointer"
                >
                  <span className="text-xs text-foreground-500">下一篇</span>
                  <p className="text-sm text-foreground-700 group-hover:text-primary-600 transition-colors line-clamp-1 mt-1">
                    {nextArticle.title}
                  </p>
                </Link>
              ) : (
                <div className="flex-1"></div>
              )}
            </div>

            {relatedArticles.length > 0 && (
              <div className="mt-12 pt-8 border-t border-background-200/70">
                <h3 className="font-heading font-semibold text-foreground-950 mb-5">
                  相关文章
                </h3>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                  {relatedArticles.map((ra) => (
                    <Link
                      key={ra.id}
                      to={`/article/${ra.slug}`}
                      className="block p-4 rounded-xl border border-background-200/70 hover:border-primary-200/70 transition-all cursor-pointer"
                    >
                      <div className="text-xs text-foreground-500 mb-1">{ra.category}</div>
                      <h4 className="text-sm font-medium text-foreground-950 line-clamp-2 hover:text-primary-600 transition-colors">
                        {ra.title}
                      </h4>
                    </Link>
                  ))}
                </div>
              </div>
            )}

            <div className="text-center mt-10">
              <Link
                to="/articles"
                className="inline-flex items-center gap-2 px-6 py-3 bg-background-100 text-foreground-700 font-medium rounded-full text-sm whitespace-nowrap cursor-pointer hover:bg-background-200 transition-colors"
              >
                <i className="ri-arrow-left-line"></i>
                返回资讯列表
              </Link>
            </div>
          </div>
        </article>
      </main>
      <Footer />
    </>
  );
}